import { COMMON_ERROR_STATUS_MAP, ORPCError } from "@orpc/client";
import { defineMeta } from "@orpc/contract";

export type ContractStage = "alpha" | "beta" | "stable";

export interface ContractService {
  group: string;
  version: `v${number}` | `v${number}alpha${number}` | `v${number}beta${number}`;
  stage: ContractStage;
  title: string;
  description: string;
}

/**
 * The `pax` route metadata plugin and its reader for one contract package. Each package
 * passes its own metadata shape, because each service authenticates different callers.
 */
export function defineRouteMetadata<Metadata extends { auth: unknown }>() {
  const [paxRoute, readPaxRouteMetadata] = defineMeta("pax", (incoming: Metadata) => incoming);

  function getPaxRouteMetadata(procedure: Parameters<typeof readPaxRouteMetadata>[0]): Metadata {
    const metadata = readPaxRouteMetadata(procedure);
    if (!metadata) throw new Error("procedure is missing Pax route metadata");
    return metadata;
  }

  return { paxRoute, getPaxRouteMetadata };
}

/** Error declarations with the default message for each code. Routes may override the message. */
export const errors = {
  badRequest: { BAD_REQUEST: { message: "The request did not match its schema." } },
  unauthorized: { UNAUTHORIZED: { message: "Valid credentials are required." } },
  forbidden: { FORBIDDEN: { message: "The credential does not grant this operation." } },
  notFound: { NOT_FOUND: { message: "The requested resource does not exist." } },
  conflict: { CONFLICT: { message: "The request conflicts with existing state." } },
  rateLimited: { TOO_MANY_REQUESTS: { message: "Too many requests; try again shortly." } },
  worldUnavailable: {
    WORLD_UNAVAILABLE: { status: 500, message: "This game's world could not be loaded." },
  },
} as const;

type CommonErrorCode = keyof typeof COMMON_ERROR_STATUS_MAP;

/**
 * An error response in oRPC's body shape, for the few routes a Worker answers outside its oRPC
 * handler, so every Pax error reads the same way. A code outside oRPC's common set names its
 * status in `init`.
 */
export function errorResponse(
  code: CommonErrorCode | (string & {}),
  message: string,
  init: ResponseInit = {},
): Response {
  const status = init.status ?? (COMMON_ERROR_STATUS_MAP as Record<string, number>)[code] ?? 500;
  return Response.json(new ORPCError(code, { message }).toJSON(), { ...init, status });
}

export function pathFor(
  template: `/${string}`,
  parameters: Readonly<Record<string, string | number>>,
): string {
  return template.replaceAll(/\{(\+?)([^}]+)\}/g, (_, _catchAll: string, name: string) => {
    const value = parameters[name];
    if (value === undefined) throw new Error(`missing path parameter ${name}`);
    return String(value)
      .split("/")
      .map(segment => encodeURIComponent(segment))
      .join("/");
  });
}
